import React from 'react';
import { Link } from 'react-router-dom';
import Badge from '../../../components/ui/Badge';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { APP_CONFIG, formatDate } from '../../../config/appConfig';
import type { CrewHoliday } from './useData';

const KIND_LABEL: Record<string, string> = {
  REGULAR: 'Regular',
  SPECIAL_NON_WORKING: 'Special non-working',
};

const RECURRENCE_LABEL: Record<string, string> = {
  FIXED_MONTH_DAY: 'Fixed month/day',
  NTH_WEEKDAY_OF_MONTH: 'Nth weekday of month',
  EASTER_OFFSET: 'Relative to Easter',
  LUNAR_NEW_YEAR: 'Lunar New Year',
  EVERY_WEEKDAY: 'Every weekday',
};

export const moduleConfig = {
  module: {
    title: 'Crew holidays',
    permissionModuleKey: 'crew_parameters',
    route: {
      list: '/parameters/crew?section=holidays',
      new: '/parameters/crew/holidays/new',
      detail: (id: number) => `/parameters/crew/holidays/${id}`,
      edit: (id: number) => `/parameters/crew/holidays/${id}/edit`,
    },
    api: {
      list: '/crew-holidays',
      detail: (id: number) => `/crew-holidays/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: { placeholder: 'Search by code or name…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['kind', 'recurrence', 'isActive'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'kind',
        label: 'Kind',
        getValue: (item: CrewHoliday) => item.kind,
        options: [
          { value: 'REGULAR', label: 'Regular' },
          { value: 'SPECIAL_NON_WORKING', label: 'Special non-working' },
        ],
      },
      {
        key: 'recurrence',
        label: 'Rule',
        getValue: (item: CrewHoliday) => item.recurrence,
        options: [
          { value: 'FIXED_MONTH_DAY', label: 'Fixed month/day' },
          { value: 'NTH_WEEKDAY_OF_MONTH', label: 'Nth weekday' },
          { value: 'EASTER_OFFSET', label: 'Easter offset' },
          { value: 'LUNAR_NEW_YEAR', label: 'Lunar New Year' },
          { value: 'EVERY_WEEKDAY', label: 'Every weekday' },
        ],
      },
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: CrewHoliday) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ],
      },
    ],
    applyFilter: (items: CrewHoliday[], filters: Record<string, string[]>) =>
      items.filter((item) => {
        if (filters.kind?.length && !filters.kind.includes(item.kind)) return false;
        if (filters.recurrence?.length && !filters.recurrence.includes(item.recurrence)) {
          return false;
        }
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      }),
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New holiday',
        showTitle: false,
        variant: 'primary' as const,
        href: '/parameters/crew/holidays/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: CrewHoliday) => item.id,
  },
  emptyStates: {
    noData: 'Add recurring crew holidays (e.g. New Year’s Day every Jan 1).',
    noSearchResults: 'No holidays match your search.',
    noFilterResults: 'No holidays match the filters.',
  },
  delete: {
    title: 'Delete holiday',
    message: 'Delete this holiday rule?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete holiday.',
    handler: async (id: number) => {
      const { deleteCrewHoliday } = await import('../../../services/mutations');
      await deleteCrewHoliday(id);
    },
  },
  utils: {
    formatDate: (date?: string) => (date ? formatDate(date) : ''),
  },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This holiday does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Holiday',
    getTitle: (item: CrewHoliday) => item.name || `Holiday #${item.id}`,
  },
  form: {
    new: {
      title: 'New holiday',
      subtitle: 'Define a recurring calendar rule for WRH and holiday pay',
      submitText: 'Create holiday',
    },
    edit: {
      title: 'Edit holiday',
      subtitle: 'Update this recurring holiday rule',
      submitText: 'Save changes',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<CrewHoliday>) => {
      const { createCrewHoliday } = await import('../../../services/mutations');
      return createCrewHoliday(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<CrewHoliday>) => {
      const { updateCrewHoliday } = await import('../../../services/mutations');
      return updateCrewHoliday(id, data as Record<string, unknown>);
    },
  },
  fields: [] as FieldConfig[],
  formLayout: { mode: 'default', sections: [] } as FormLayout,
};

const Text = ({ value }: { value?: string | number | null }) => (
  <span className="text-sm">{value ?? ''}</span>
);

export const getColumns = (date: (value?: string) => string): Column<CrewHoliday>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v) => <Text value={v} /> },
  { key: 'name', header: 'Name', sortable: true, render: (v) => <Text value={v} /> },
  {
    key: 'ruleLabel',
    header: 'Rule',
    sortable: false,
    render: (_v, row) => <Text value={row?.ruleLabel ?? RECURRENCE_LABEL[String(row?.recurrence)] ?? ''} />,
  },
  {
    key: 'resolvedDate',
    header: 'This year',
    sortable: false,
    render: (_v, row) =>
      row?.recurrence === 'EVERY_WEEKDAY' ? (
        <Text value={row.ruleLabel ?? 'Weekly'} />
      ) : (
        date(row?.resolvedDate ?? undefined)
      ),
  },
  {
    key: 'kind',
    header: 'Kind',
    sortable: true,
    render: (v) => <Text value={KIND_LABEL[String(v)] ?? String(v ?? '')} />,
  },
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={(v ?? row?.isActive) ? 'success' : 'danger'}>
        {(v ?? row?.isActive) ? 'Yes' : 'No'}
      </Badge>
    ),
  },
];

export const getDetailSections = (item: CrewHoliday, date: (value?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Code', value: item.code },
      { label: 'Name', value: item.name },
      { label: 'Kind', value: KIND_LABEL[item.kind] ?? item.kind },
      { label: 'Country', value: item.countryCode },
      { label: 'Rule', value: item.ruleLabel || RECURRENCE_LABEL[item.recurrence] || item.recurrence },
      {
        label: `Date in ${item.resolvedYear ?? new Date().getFullYear()}`,
        value:
          item.recurrence === 'EVERY_WEEKDAY'
            ? item.ruleLabel || 'Weekly'
            : item.resolvedDate
              ? date(item.resolvedDate)
              : '—',
      },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Sort order', value: String(item.sortOrder) },
      { label: 'Notes', value: item.notes || '' },
      { label: 'Created', value: date(item.createdAt) },
      { label: 'Updated', value: date(item.updatedAt) },
    ],
  },
];

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<CrewHoliday> => ({
  code: '',
  name: '',
  kind: 'REGULAR',
  countryCode: 'PH',
  recurrence: 'FIXED_MONTH_DAY',
  month: 1,
  dayOfMonth: 1,
  weekday: 1,
  weekOrdinal: -1,
  easterOffset: -2,
  sortOrder: 0,
  isActive: true,
  notes: '',
});

export const mapItemToFormData = (item: CrewHoliday): Partial<CrewHoliday> => ({
  code: item.code,
  name: item.name,
  kind: item.kind,
  countryCode: item.countryCode,
  recurrence: item.recurrence,
  month: item.month,
  dayOfMonth: item.dayOfMonth,
  weekday: item.weekday,
  weekOrdinal: item.weekOrdinal,
  easterOffset: item.easterOffset,
  sortOrder: item.sortOrder,
  isActive: item.isActive,
  notes: item.notes ?? '',
});

export const getToolbarIcon = (name: string) =>
  name === 'add' ? <span className="text-lg leading-none">+</span> : null;

export const getTableActions = (item: CrewHoliday, onDelete: (id: number) => void) => (
  <div className="flex gap-2">
    <Link to={moduleConfig.module.route.detail(item.id)} className="text-blue-600">
      View
    </Link>
    <Link to={moduleConfig.module.route.edit(item.id)} className="text-blue-600">
      Edit
    </Link>
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onDelete(item.id);
      }}
      className="text-red-600"
    >
      Delete
    </button>
  </div>
);

export type ModuleConfig = typeof moduleConfig;
